import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, ShieldCheck, UserRound, Cake, Loader2 } from 'lucide-react';
import { useCustomerAuth } from './CustomerAuthContext';
type Step = 'phone' | 'otp' | 'profile';
export function AuthSheet({
  open,
  accent,
  itemName,
  onClose,
  onAuthenticated
}: {
  open: boolean;
  accent: string;
  itemName?: string | null;
  onClose: () => void;
  onAuthenticated: () => void;
}) {
  const {
    sendOtp,
    verifyOtp,
    completeSignup
  } = useCustomerAuth();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Reset everything whenever the sheet is freshly opened.
  useEffect(() => {
    if (open) {
      setStep('phone');
      setPhone('');
      setOtp(['', '', '', '', '', '']);
      setFirstName('');
      setLastName('');
      setBirthday('');
      setError(null);
      setResendIn(0);
    }
  }, [open]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);
  const phoneValid = phone.replace(/\D/g, '').length >= 9;
  const otpValue = otp.join('');
  const handleSendOtp = async () => {
    if (!phoneValid || loading) return;
    setError(null);
    setLoading(true);
    try {
      await sendOtp(`+251${phone.replace(/\D/g, '')}`);
      setStep('otp');
      setResendIn(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };
  const handleOtpChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setOtp(prev => {
      const next = [...prev];
      next[i] = digit;
      return next;
    });
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const handleVerify = async () => {
    if (otpValue.length < 6 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const {
        isNewUser
      } = await verifyOtp(otpValue);
      if (isNewUser) {
        setStep('profile');
      } else {
        onAuthenticated();
      }
    } catch {
      setError('That code didn’t work. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleComplete = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    completeSignup(firstName.trim(), lastName.trim(), birthday);
    onAuthenticated();
  };
  const title = step === 'profile' ? 'Almost there' : itemName ? 'Sign in to start your order' : 'Sign in or sign up';
  const subtitle = step === 'phone' ? itemName ? `Sign in to add “${itemName}” and place your order.` : 'Enter your phone number to order and track your meals.' : step === 'otp' ? `We sent a 6-digit code to +251 ${phone}.` : 'Tell us a little about you to finish your account.';
  return <AnimatePresence data-id="element-253">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40" data-id="element-254" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 34
      }} className="fixed bottom-0 inset-x-0 z-50 bg-white rounded-t-3xl shadow-2xl max-w-md mx-auto" role="dialog" aria-modal="true" data-id="element-255">
            <div className="flex justify-center pt-3" data-id="element-256">
              <span className="w-10 h-1.5 rounded-full bg-gray-200" data-id="element-257" />
            </div>

            <div className="flex items-start justify-between px-5 pt-3" data-id="element-258">
              <div className="pr-8" data-id="element-259">
                <h2 className="text-lg font-extrabold text-gray-900 tracking-tight" data-id="element-260">
                  {title}
                </h2>
                <p className="text-sm text-gray-500 mt-1" data-id="element-261">{subtitle}</p>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0" data-id="element-262">
                <X size={20} data-id="element-263" />
              </button>
            </div>

            <div className="px-5 pb-8 pt-5" data-id="element-264">
              {/* Step progress */}
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-265">
                {(['phone', 'otp', 'profile'] as Step[]).map((s, i) => {
              const order = ['phone', 'otp', 'profile'];
              const done = order.indexOf(step) > i;
              const current = step === s;
              return <div key={s} className={`h-1.5 rounded-full transition-all ${current ? `w-8 bg-${accent}-500` : done ? `w-4 bg-${accent}-400` : 'w-4 bg-gray-200'}`} data-id="element-266" />;
            })}
              </div>

              <AnimatePresence mode="wait" data-id="element-267">
                {step === 'phone' && <motion.div key="phone" initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.18
            }} className="space-y-4" data-id="element-268">
                    <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-269">
                      <span className="flex items-center gap-1.5 px-3 py-3.5 bg-gray-50 text-gray-600 text-sm font-semibold border-r border-gray-200" data-id="element-270">
                        <Phone size={14} data-id="element-271" />
                        +251
                      </span>
                      <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 9))} onKeyDown={e => e.key === 'Enter' && handleSendOtp()} inputMode="numeric" autoFocus placeholder="9XX XXX XXX" className="flex-1 px-3 py-3.5 text-sm focus:outline-none" data-id="element-272" />
                    </div>
                    <SheetButton accent={accent} onClick={handleSendOtp} disabled={!phoneValid || loading} loading={loading} data-id="element-273">
                      Send code
                    </SheetButton>
                  </motion.div>}

                {step === 'otp' && <motion.div key="otp" initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.18
            }} className="space-y-4" data-id="element-274">
                    <div className="flex justify-center mb-1" data-id="element-275">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center bg-${accent}-50 text-${accent}-600`} data-id="element-276">
                        <ShieldCheck size={22} data-id="element-277" />
                      </div>
                    </div>
                    <div className="flex justify-between gap-2" data-id="element-278">
                      {otp.map((d, i) => <input key={i} ref={el => otpRefs.current[i] = el} value={d} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)} inputMode="numeric" maxLength={1} className={`w-11 sm:w-12 h-14 text-center text-xl font-bold rounded-xl border ${d ? `border-${accent}-400 bg-${accent}-50/40` : 'border-gray-200'} focus:outline-none focus:border-${accent}-500 focus:ring-2 focus:ring-${accent}-100 transition`} data-id="element-279" />)}
                    </div>
                    <SheetButton accent={accent} onClick={handleVerify} disabled={otpValue.length < 6 || loading} loading={loading} data-id="element-280">
                      Verify
                    </SheetButton>
                    <div className="text-center" data-id="element-281">
                      {resendIn > 0 ? <span className="text-xs text-gray-400" data-id="element-282">
                          Resend code in {resendIn}s
                        </span> : <button onClick={handleSendOtp} className={`text-xs font-semibold text-${accent}-600 hover:underline`} data-id="element-283">
                          Resend code
                        </button>}
                    </div>
                  </motion.div>}

                {step === 'profile' && <motion.div key="profile" initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.18
            }} className="space-y-4" data-id="element-284">
                    <div className="flex gap-3" data-id="element-285">
                      <div className="flex-1 flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-286">
                        <span className="px-3 py-3.5 text-gray-400 border-r border-gray-200" data-id="element-287">
                          <UserRound size={16} data-id="element-288" />
                        </span>
                        <input value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus placeholder="First name" className="flex-1 px-3 py-3.5 text-sm focus:outline-none w-full min-w-0" data-id="element-289" />
                      </div>
                      <div className="flex-1 flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-290">
                        <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" className="flex-1 px-3 py-3.5 text-sm focus:outline-none w-full min-w-0" data-id="element-291" />
                      </div>
                    </div>
                    <div data-id="element-292">
                      <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-293">
                        <span className="px-3 py-3.5 text-gray-400 border-r border-gray-200" data-id="element-294">
                          <Cake size={16} data-id="element-295" />
                        </span>
                        <input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} className="flex-1 px-3 py-3.5 text-sm focus:outline-none text-gray-700" data-id="element-296" />
                      </div>
                      <span className="text-[11px] text-gray-400 mt-1.5 block" data-id="element-297">
                        Birthday is optional — we’ll send you a treat.
                      </span>
                    </div>
                    <SheetButton accent={accent} onClick={handleComplete} data-id="element-298">
                      Create account &amp; add item
                    </SheetButton>
                  </motion.div>}
              </AnimatePresence>

              {error && <p className="text-xs text-rose-600 font-medium mt-3 text-center" data-id="element-299">
                  {error}
                </p>}

              <p className="text-[11px] text-gray-400 text-center mt-5 leading-relaxed" data-id="element-300">
                By continuing you agree to our Terms and Privacy Policy.
              </p>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function SheetButton({
  accent,
  children,
  onClick,
  disabled,
  loading
}: {
  accent: string;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return <button onClick={onClick} disabled={disabled} className={`w-full py-3.5 rounded-xl text-sm font-bold text-white transition-colors flex items-center justify-center gap-2 ${disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : `bg-${accent}-500 hover:bg-${accent}-600`}`} data-id="element-301">
      {loading && <Loader2 size={16} className="animate-spin" data-id="element-302" />}
      {children}
    </button>;
}